import React, { useState, useEffect, useCallback, useContext } from "react";
import { FaArrowLeft, FaRegEye, FaDownload } from "react-icons/fa6";
import { FaCheckCircle } from "react-icons/fa";
import { toast } from "react-toastify";
import { api, API_BASE_URL, API_DEPLOYED_URL } from "../../config/api";
import { CurrencyContext } from "../../context/CurrencyContext";
import { Select, message } from "antd";
import Modal from "react-modal";
import ExternalWarningPopup from './ExternalWarningPopup'; // New warning popup for external estimates
import EstimateSummaryWithDHL from '../forms/EstimateSummaryWithDHL';
import { calculateServiceValue } from '../../utils/estimateUtils';
import DHLService from './DHLService';
import EstimateFormDetails from './EstimateFormDetails';
import EstimateFormDimensions from './EstimateFormDimensions';
import EstimateFormItems from './EstimateFormItems';
import EstimateFormOptions from './EstimateFormOptions';
import {
    handleInputChange, handleServiceInputChange,
    handleAntdServiceInputChange, handleAddMore, handleRemoveService, handleInsuranceChange, fetchAdminEstimate, needsStateSelection,
    fetchDisabledCountries, fetchEccnCodes, calculateDHLTaxes, renderEccnOption
} from './estimateFormHandlers';
import { getAlpha2Code } from '../../lib/helper/countryToAphlaCode';

Modal.setAppElement("#root");

// Unit conversion functions
const convertLbsToKg = (lbs) => {
    return lbs * 0.453592;
};

const convertInchesToCm = (inches) => {
    return inches * 2.54;
};

// Helper function to get converted weight in kg
const getWeightInKg = (weight) => {
    if (!weight || !weight.value) return 0;
    const value = parseFloat(weight.value);
    return weight.unit === 'lbs' ? convertLbsToKg(value) : value;
};

// Helper function to get converted dimension in cm
const getDimensionInCm = (dimension) => {
    if (!dimension || !dimension.value) return 0;
    const value = parseFloat(dimension.value);
    return dimension.unit === 'in' ? convertInchesToCm(value) : value;
};

const EstimateFormComponent = ({ isGuest = false, linkId, onSubmit, initialData = {}, isNewEstimate = true, estimateId,
    onBack, fetchEstimates, isVisible, onClose }) => {

    const { user } = { uid: "guest", name: "Guest", email: "" }; // Placeholder user object
    const { currencies } = useContext(CurrencyContext);

    // State declarations
    const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
    const [externalWarningPopup, setExternalWarningPopup] = useState({ isOpen: false, message: "" }); // New warning popup state
    const [isPdfModalVisible, setIsPdfModalVisible] = useState(false);
    const [currentPdfUrl, setCurrentPdfUrl] = useState(null);
    const [formData, setFormData] = useState({
        userId: isGuest ? "guest" : user?.uid || "",
        userName: isGuest ? "" : user?.name || "",
        userCity: user?.city || "",
        userCountry: user?.country || "",
        postalCode: user?.postalCode || "",
        insuranceSelected: "NO",
        email: isGuest ? "" : user?.email || "",
        currency: "USD",
        itemsCurrency: "USD",
        selectedService: "IOR",
        hsCode: "",
        dimensions: [
            {
                weight: { value: "", unit: "kg" },
                length: { value: "", unit: "cm" },
                width: { value: "", unit: "cm" },
                height: { value: "", unit: "cm" },
                quantity: { value: "" },
            },
        ],
        originCountry: "",
        originState: "",
        destinationCountry: "",
        destinationState: "",
        destinationCity: "",
        originCity: "",
        status: false,
        pending: "",
        requiredDoc: "not_Required",
        items: [
            {
                itemName: "",
                itemHSCode: "",
                itemValue: "",
                countryOrigin: "",
                quantity: "",
                hasHSCode: null,
                itemNew: true,
                hsCodeSource: "database",
            },
        ],
    });
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [calculatedValues, setCalculatedValues] = useState({});
    const [adminEstimate, setAdminEstimate] = useState(null);
    const [showEstimateResults, setShowEstimateResults] = useState(false);
    const [isReadOnly, setIsReadOnly] = useState(false);
    const [buttonText, setButtonText] = useState(isNewEstimate ? "Get Estimate" : "Update Estimate");
    const [estimateSummary, setEstimateSummary] = useState(null);
    const [isHSCodeRelated, setIsHSCodeRelated] = useState(false);
    const [clientEstimateSummary, setClientEstimateSummary] = useState(null);
    const [itemsTotalQty, setItemsTotalQty] = useState(0);
    const [totalItemsValue, setTotalItemsValue] = useState(0);
    const [totalItemsQuantity, setTotalItemsQuantity] = useState(0);
    const [totalDimensionsWeight, setTotalDimensionsWeight] = useState(0);
    const [totalDimensionsLength, setTotalDimensionsLength] = useState(0);
    const [totalDimensionsWidth, setTotalDimensionsWidth] = useState(0);
    const [totalDimensionsHeight, setTotalDimensionsHeight] = useState(0);
    const [insurance, setInsurance] = useState(0);
    const [airfreight, setAirfreight] = useState(0);
    const [cif, setCif] = useState(0);
    const [dhlEstimation, setDhlEstimation] = useState(null);
    const [eccnCodes, setEccnCodes] = useState([]);
    const [duty, setDuty] = useState(0);
    const [collectionValue, setCollectionValue] = useState(0);
    const [disabledCountries, setDisabledCountries] = useState([]);
    const [fetchDHL, setFetchDHL] = useState(false);
    const [dhlResult, setDhlResult] = useState(null);

    useEffect(() => {
        if (Object.keys(initialData).length > 0) {
            setFormData(prev => ({
                ...prev,
                ...initialData,
                dimensions: initialData.dimensions || prev.dimensions,
                items: initialData.items ? initialData.items.map(item => ({
                    ...item,
                    hsCodeSource: item.hsCodeSource || "database"
                })) : prev.items,
            }));
            if (initialData.estimateSummary) {
                setEstimateSummary(initialData.estimateSummary);
                setShowEstimateResults(true);
            }
        }
    }, [initialData]);

    useEffect(() => {
        const loadData = async () => {
            const disabled = await fetchDisabledCountries(linkId);
            setDisabledCountries(disabled);
            const eccn = await fetchEccnCodes(linkId);
            setEccnCodes(eccn);
        };
        loadData();
    }, [linkId]);

    useEffect(() => {
        // Add more comprehensive validation and debouncing to prevent unnecessary API calls
        const fetchEstimateWithDebounce = async () => {
            // Ensure all required fields are present and valid
            if (
                formData.originCountry &&
                formData.destinationCountry &&
                formData.selectedService &&
                formData.itemsCurrency &&
                formData.dimensions?.[0]?.weight?.unit &&
                linkId && // Ensure linkId exists
                !loading && // Don't fetch if already loading
                formData.originCountry !== formData.destinationCountry // Basic validation
            ) {
                // Add a debounce timeout to prevent rapid successive calls
                const timeoutId = setTimeout(() => {
                    fetchAdminEstimate(
                        formData.originCountry,
                        formData.destinationCountry,
                        formData.selectedService,
                        formData.itemsCurrency,
                        "kg", // ALWAYS pass "kg" for external estimates
                        setAdminEstimate,
                        setFormData,
                        loading,
                        setLoading,
                        linkId
                    );
                }, 500); // 500ms debounce delay

                // Cleanup function to clear timeout
                return () => clearTimeout(timeoutId);
            }
        };

        fetchEstimateWithDebounce();
    }, [
        formData.originCountry,
        formData.destinationCountry,
        formData.selectedService,
        formData.itemsCurrency,
        formData.dimensions?.[0]?.weight?.unit,
        linkId,
        loading
    ]);

    useEffect(() => {
        const calculateTotalDimensions = () => {
            // Use conversion functions for accurate calculations in kg and cm
            const totalWeight = formData.dimensions.reduce((sum, dim) => {
                const weightInKg = getWeightInKg(dim.weight);
                const quantity = parseFloat(dim.quantity.value || 0);
                return sum + (weightInKg * quantity);
            }, 0);

            const totalLength = formData.dimensions.reduce((sum, dim) => {
                return sum + getDimensionInCm(dim.length);
            }, 0);

            const totalWidth = formData.dimensions.reduce((sum, dim) => {
                return sum + getDimensionInCm(dim.width);
            }, 0);

            const totalHeight = formData.dimensions.reduce((sum, dim) => {
                return sum + getDimensionInCm(dim.height);
            }, 0);

            setTotalDimensionsWeight(totalWeight);
            setTotalDimensionsLength(totalLength);
            setTotalDimensionsWidth(totalWidth);
            setTotalDimensionsHeight(totalHeight);
        };
        calculateTotalDimensions();
    }, [formData.dimensions]);

    useEffect(() => {
        const calculateTotalValue = () => {
            const total = formData.items.reduce((sum, item) => {
                const value = parseFloat(item.itemValue) || 0;
                const quantity = parseFloat(item.quantity) || 0;
                return sum + (value * quantity);
            }, 0);
            const totalQty = formData.items.reduce((sum, item) => sum + (parseFloat(item.quantity) || 0), 0);
            setTotalItemsValue(total);
            setItemsTotalQty(totalQty);
        };
        calculateTotalValue();
    }, [formData.items]);

    useEffect(() => {
        const calculateTotalQuantity = () => {
            const total = formData.dimensions.reduce((sum, dim) => sum + (parseFloat(dim.quantity.value) || 0), 0);
            setTotalItemsQuantity(total);
        };
        calculateTotalQuantity();
    }, [formData.dimensions]);

    useEffect(() => {
        const calculateInsurance = () => {
            const calculatedInsurance = formData.insuranceSelected === "YES" ? totalItemsValue * 0.0075 : 0;
            setInsurance(calculatedInsurance);
            const updatedCIF = totalItemsValue + airfreight + calculatedInsurance + duty + collectionValue;
            setCif(updatedCIF);
        };
        calculateInsurance();
    }, [formData.insuranceSelected, totalItemsValue, airfreight, duty, collectionValue]);

    useEffect(() => {
        const calculateAirfreightCost = () => {
            // Use converted dimensions in CM for volumetric weight calculation
            const totalVolumetricWeight = formData.dimensions.reduce((sum, dim) => {
                const lengthCm = getDimensionInCm(dim.length);
                const widthCm = getDimensionInCm(dim.width);
                const heightCm = getDimensionInCm(dim.height);
                const quantity = parseFloat(dim.quantity.value || 0);

                let volWeight = (lengthCm * widthCm * heightCm) / 6000;
                return sum + (volWeight * quantity);
            }, 0);

            // Take the maximum between volumetric weight and actual weight (both in kg)
            const chargeableWeight = Math.max(totalVolumetricWeight, totalDimensionsWeight);

            let airfreightCost;
            if (chargeableWeight < 360) {
                airfreightCost = 6.50 * chargeableWeight + 250;
            } else if (chargeableWeight >= 360 && chargeableWeight <= 1080) {
                airfreightCost = 5.00 * chargeableWeight + 250;
            } else {
                airfreightCost = 4.00 * chargeableWeight + 250;
            }
            setAirfreight(airfreightCost);
        };
        calculateAirfreightCost();
    }, [formData.dimensions, totalDimensionsWeight]);

    useEffect(() => {
        if (formData.selectedTemplate && formData.selectedTemplate.services) {
            const collectionService = formData.selectedTemplate.services.find(
                service => service.description.toUpperCase() === "COLLECTION"
            );
            if (collectionService) {
                const calculatedCollection = calculateServiceValue(collectionService, formData, calculatedValues, totalItemsValue,
                    totalDimensionsWeight, totalDimensionsLength, totalDimensionsWidth, totalDimensionsHeight, 0, insurance,
                    totalItemsQuantity, itemsTotalQty);
                setCollectionValue(calculatedCollection);
            } else {
                setCollectionValue(0);
            }
        }
    }, [formData.selectedTemplate, formData, calculatedValues, totalItemsValue, totalDimensionsWeight, totalDimensionsLength,
        totalDimensionsWidth, totalDimensionsHeight, insurance, totalItemsQuantity, itemsTotalQty]);

    useEffect(() => {
        if (formData.selectedTemplate && formData.selectedTemplate.services) {
            const userCurrency = isGuest ? "USD" : user?.currency || "EUR";
            const currencyData = currencies.find(curr => curr.name === userCurrency) || { name: "EUR", exchangeRateUSD: 1.07 };
            const exchangeRateUSD = currencyData.exchangeRateUSD || 1;

            const newCalculatedValues = {};
            formData.selectedTemplate.services.forEach(service => {
                newCalculatedValues[service.description] = calculateServiceValue(
                    service,
                    formData,
                    newCalculatedValues,
                    formData.items.reduce((sum, item) => sum + (parseFloat(item.itemValue) * exchangeRateUSD), 0),
                    totalDimensionsWeight,
                    totalDimensionsLength,
                    totalDimensionsWidth,
                    totalDimensionsHeight,
                    cif,
                    insurance,
                    totalItemsQuantity,
                    itemsTotalQty
                );
            });
            setCalculatedValues(newCalculatedValues);

            const updatedSummary = {
                total: Object.values(newCalculatedValues).reduce((sum, value) => sum + value, 0),
                currency: formData.airfreightCurrency || "USD",
                services: formData.selectedTemplate.services.map(service => ({
                    description: service.description,
                    value: newCalculatedValues[service.description] || 0,
                })),
            };
            setEstimateSummary(updatedSummary);
        }
    }, [formData, adminEstimate, cif, insurance, totalItemsQuantity, itemsTotalQty, totalDimensionsWeight,
        totalDimensionsLength, totalDimensionsWidth, totalDimensionsHeight]);

    useEffect(() => {
        if (formData.selectedTemplate && formData.selectedTemplate.services) {
            const hasHSCodeFormula = formData.selectedTemplate.services.some(
                service =>
                    service.values &&
                    (service.values.includes("HSCode Count") || service.values.includes("HSCode Starts with"))
            );
            setIsHSCodeRelated(hasHSCodeFormula);
        }
    }, [formData.selectedTemplate]);

    useEffect(() => {
        if (dhlResult) {
            proceedWithCalculations(dhlResult.dhlData, dhlResult.dutyValue);
        }
    }, [dhlResult]);

    const validateForm = () => {
        const newErrors = {};
        let hasErrors = false;
        const isEmpty = value => {
            if (value === null || value === undefined) return true;
            if (typeof value === "string") return value.trim() === "";
            if (typeof value === "number") return isNaN(value) || value === 0;
            return false;
        };
        if (isGuest) {
            if (isEmpty(formData.userName)) newErrors.userName = true;
            if (isEmpty(formData.email)) newErrors.email = true;
            if (newErrors.userName || newErrors.email) hasErrors = true;
        }
        formData.dimensions.forEach((dimension, index) => {
            ["weight", "length", "width", "height", "quantity"].forEach(field => {
                if (isEmpty(dimension[field]?.value)) {
                    newErrors[`${field}-${index}`] = true;
                    hasErrors = true;
                }
            });
        });
        const isValidHSCode = code => /^\d{6,}$/.test(code);
        const fieldsToValidate = ["destinationCity", "originCity", "originCountry", "destinationCountry"];
        if (formData.selectedService !== "EOR") fieldsToValidate.push("requiredDoc");
        if ((formData.selectedService === "EOR" || formData.selectedService === "EOR & IOR") && isEmpty(formData.eccnCode)) {
            newErrors["eccnCode"] = true;
            hasErrors = true;
        }
        if (needsStateSelection(formData.originCountry) && isEmpty(formData.originState)) {
            newErrors["originState"] = true;
            hasErrors = true;
        }
        if (needsStateSelection(formData.destinationCountry) && isEmpty(formData.destinationState)) {
            newErrors["destinationState"] = true;
            hasErrors = true;
        }
        fieldsToValidate.forEach(key => {
            if (isEmpty(formData[key])) {
                newErrors[key] = true;
                hasErrors = true;
            }
        });
        if (formData.items.length === 0) {
            newErrors.items = "At least one item is required.";
            hasErrors = true;
        } else {
            formData.items.forEach((item, index) => {
                ["itemName", "quantity", "itemValue", "countryOrigin", "hasHSCode", "itemNew"].forEach(field => {
                    if (isEmpty(item[field])) {
                        newErrors[`${field}-${index}`] = true;
                        hasErrors = true;
                    }
                });
                if (item.hasHSCode === true) {
                    if (isEmpty(item.itemHSCode)) {
                        newErrors[`itemHSCode-${index}`] = true;
                        hasErrors = true;
                    } else {
                        // Enhanced validation for manual entry
                        if (item.hsCodeSource === 'manual') {
                            const hsCode = item.itemHSCode.toString();
                            if (hsCode.length < 6) {
                                newErrors[`itemHSCode-${index}`] = "HS Code must be at least 6 characters long.";
                                hasErrors = true;
                            } else {
                                const first6 = hsCode.substring(0, 6);
                                if (!/^\d{6}$/.test(first6)) {
                                    newErrors[`itemHSCode-${index}`] = "First 6 digits of HS Code must be numbers only.";
                                    hasErrors = true;
                                }
                            }
                        } else {
                            // Database validation (existing logic)
                            if (!isValidHSCode(item.itemHSCode)) {
                                newErrors[`itemHSCode-${index}`] = "HS Code must be at least 6 digits long.";
                                hasErrors = true;
                            }
                        }
                    }
                }
            });
        }
        setErrors(newErrors);
        return hasErrors;
    };

    const handleGetEstimate = async () => {
        if (buttonText === "Proceed") {
            if (onBack) onBack();
            onClose();
            return;
        }
        const hasErrors = validateForm();
        if (hasErrors) {
            toast.error("Please fill all mandatory fields.");
            return;
        }
        if ((formData.selectedService === "EOR" || formData.selectedService === "EOR & IOR") && formData.eccnCode) {
            const selectedEccnObj = eccnCodes.find(eccn => eccn.eccn_code === formData.eccnCode);
            if (selectedEccnObj && selectedEccnObj.restrictions) {
                setExternalWarningPopup({
                    isOpen: true,
                    message: "The selected ECCN code is restricted. Contact info@oneunionsolutions.com for assistance.",
                });
                return;
            }
        }
        const hasInvalidHSCode = formData.items.some(item => item.hasHSCode === false);
        const hasNonNewItem = formData.items.some(item => item.itemNew === false);
        if (hasInvalidHSCode) {
            setExternalWarningPopup({
                isOpen: true,
                message: "We only accept items with HS code. Contact info@oneunionsolutions.com for used items.",
            });
            return;
        }
        if (hasNonNewItem) {
            toast.error("Please ensure all items are new.");
            return;
        }
        setLoading(true);
        try {
            const isDisabled = disabledCountries.some(country =>
                country.countryName.toLowerCase() === formData.destinationCountry.toLowerCase()
            );
            if (isDisabled) {
                setExternalWarningPopup({
                    isOpen: true,
                    message: `Currently Estimation not Available for ${formData.destinationCountry}. Please contact our team through info@oneunionsolutions.com with your request.`,
                });
                setLoading(false); // Reset loading state
                return;
            }

            // First, try to fetch admin estimate
            await fetchAdminEstimate(formData.originCountry, formData.destinationCountry, formData.selectedService,
                formData.itemsCurrency, "kg", setAdminEstimate, setFormData, loading, setLoading, linkId);

            // Check if adminEstimate is available after the fetch
            if (!adminEstimate) {
                setExternalWarningPopup({
                    isOpen: true,
                    message: `Currently Estimation not Available for ${formData.destinationCountry}. Please contact our team through info@oneunionsolutions.com with your request.`,
                });
                setLoading(false); // Reset loading state
                return;
            }

            if (formData.selectedService !== "EOR" && !fetchDHL) {
                setFetchDHL(true);
            } else if (formData.selectedService === "EOR") {
                proceedWithCalculations(null, 0);
            }
        } catch (error) {
            toast.error(`Failed to submit estimate: ${error.message}`);
            setLoading(false);
        }
    };

    const proceedWithCalculations = async (dhlData, dutyValue) => {
        const userCurrency = isGuest ? "USD" : user?.currency || "EUR";
        const currencyData = currencies.find(curr => curr.name === userCurrency) || { name: "EUR", exchangeRateUSD: 1.07 };
        const exchangeRateUSD = currencyData.exchangeRateUSD || 1;
        setDhlEstimation(dhlData);
        setDuty(dutyValue);

        const calculatedInsurance = formData.insuranceSelected === "YES" ? totalItemsValue * 0.0075 : 0;
        setInsurance(calculatedInsurance);

        // Use converted dimensions in CM for volumetric weight calculation
        const totalVolumetricWeight = formData.dimensions.reduce((sum, dim) => {
            const lengthCm = getDimensionInCm(dim.length);
            const widthCm = getDimensionInCm(dim.width);
            const heightCm = getDimensionInCm(dim.height);
            const quantity = parseFloat(dim.quantity.value || 0);

            let volWeight = (lengthCm * widthCm * heightCm) / 6000;
            return sum + (volWeight * quantity);
        }, 0);

        // Take the maximum between volumetric weight and actual weight (both in kg)
        const chargeableWeight = Math.max(totalVolumetricWeight, totalDimensionsWeight);

        let airfreightCost;
        if (chargeableWeight < 360) {
            airfreightCost = 6.50 * chargeableWeight + 250;
        } else if (chargeableWeight >= 360 && chargeableWeight <= 1080) {
            airfreightCost = 5.00 * chargeableWeight + 250;
        } else {
            airfreightCost = 4.00 * chargeableWeight + 250;
        }
        setAirfreight(airfreightCost);

        const calculatedCIF = parseFloat(totalItemsValue || 0) + airfreightCost + calculatedInsurance + dutyValue + collectionValue;
        setCif(calculatedCIF);

        let dhlTaxes = [];
        let dhlTaxTotal = 0;
        if (formData.selectedService !== "EOR" && dhlData && calculatedCIF !== undefined) {
            const countryCode = getAlpha2Code(formData.destinationCountry);
            ({ dhlTaxes, dhlTaxTotal } = calculateDHLTaxes(dhlData, calculatedCIF, totalItemsValue, countryCode));
        }

        const newCalculatedValues = {};
        if (formData.selectedTemplate && formData.selectedTemplate.services) {
            formData.selectedTemplate.services.forEach(service => {
                newCalculatedValues[service.description] = calculateServiceValue(service, formData, newCalculatedValues, totalItemsValue,
                    totalDimensionsWeight, totalDimensionsLength, totalDimensionsWidth, totalDimensionsHeight, calculatedCIF,
                    calculatedInsurance, totalItemsQuantity, itemsTotalQty);
            });
        }
        setCalculatedValues(newCalculatedValues);

        const serviceTotal = Object.values(newCalculatedValues).reduce((sum, value) => sum + (parseFloat(value) || 0), 0);
        const estimateTotal = serviceTotal + parseFloat(dhlTaxTotal || 0);

        const estimateData = {
            originCity: formData.originCity,
            destinationCity: formData.destinationCity,
            originState: formData.originState,
            destinationState: formData.destinationState,
            dimensions: formData.dimensions.map(dimension => ({
                weight: dimension.weight,
                length: dimension.length,
                width: dimension.width,
                height: dimension.height,
                quantity: dimension.quantity,
            })),
            originCountry: formData.originCountry,
            destinationCountry: formData.destinationCountry,
            userId: formData.userId,
            userName: formData.userName,
            userCity: formData.userCity,
            userCountry: formData.userCountry,
            postalCode: formData.postalCode,
            email: formData.email,
            itemsCurrency: formData.itemsCurrency,
            selectedService: formData.selectedService,
            eccnCode: formData.eccnCode,
            insuranceSelected: formData.insuranceSelected,
            requiredDoc: formData.requiredDoc,
            items: formData.items.map(item => ({
                itemName: item.itemName,
                itemHSCode: item.itemHSCode,
                itemValue: item.itemValue,
                countryOrigin: item.countryOrigin,
                quantity: item.quantity,
                hasHSCode: item.hasHSCode,
                itemNew: item.itemNew,
                hsCodeSource: item.hsCodeSource,
            })),
            estimationAccept: false,
            dhlEstimation: dhlData,
            estimateSummary: formData.selectedTemplate && formData.selectedTemplate.services ?
                {
                    total: estimateTotal,
                    currency: formData.airfreightCurrency || "USD",
                    services: formData.selectedTemplate.services.map(service => ({
                        description: service.description,
                        value: newCalculatedValues[service.description] || 0,
                    })),
                    dhlTaxes: dhlTaxes,
                } :
                {
                    total: parseFloat(dhlTaxTotal || 0),
                    currency: userCurrency,
                    services: [],
                    dhlTaxes: dhlTaxes,
                },
            countryCodes: `${getAlpha2Code(formData.originCountry)}-${getAlpha2Code(formData.destinationCountry)}`,
            ...(isGuest ? { isExternal: true, externalLinkId: linkId } : {}),
        };

        let response;
        let newEstimateId;
        if (isGuest) {
            response = await api.post(`/external-links/${linkId}/estimates`, estimateData);
            newEstimateId = response.data.data._id;
        } else if (isNewEstimate) {
            response = await api.post(`${API_BASE_URL}/clientestimates`, estimateData);
            newEstimateId = response.data.data.estimateId;
        } else {
            estimateData.estimationAccept = false;
            response = await api.put(`${API_BASE_URL}/clientestimates/id/${estimateId}`, estimateData);
            newEstimateId = estimateId;
        }

        onSubmit(response.data);
        setIsSuccessModalOpen(true);
        setIsReadOnly(true);
        setButtonText("Proceed");
        setShowEstimateResults(true);
        setLoading(false);
    };

    const renderEstimateSummary = () => (
        <EstimateSummaryWithDHL
            clientEstimateSummary={clientEstimateSummary}
            dhlEstimation={dhlEstimation}
            cif={cif}
            totalItemsValue={totalItemsValue}
            formData={formData}
            showEstimateResults={showEstimateResults}
            isHSCodeRelated={isHSCodeRelated}
        />
    );

    const onDimensionChange = (index, field, value, isUnit) => {
        setFormData(prev => {
            const newDimensions = [...prev.dimensions];
            if (field === "quantity") {
                newDimensions[index][field] = { value };
            } else {
                if (isUnit) {
                    if (field === "height") {
                        // If height's unit changes, apply it to length and width of the same entry
                        newDimensions[index].length = { ...newDimensions[index].length, unit: value };
                        newDimensions[index].width = { ...newDimensions[index].width, unit: value };
                        newDimensions[index].height = { ...newDimensions[index].height, unit: value };
                    } else {
                        // For other fields (e.g., weight), just update its own unit
                        newDimensions[index][field] = { ...newDimensions[index][field], unit: value };
                    }
                } else {
                    newDimensions[index][field] = { ...newDimensions[index][field], value };
                }
            }
            return { ...prev, dimensions: newDimensions };
        });
    };

    const onAddDimension = () => {
        setFormData(prev => {
            const lastDimension = prev.dimensions[prev.dimensions.length - 1];
            const currentWeightUnit = lastDimension.weight.unit;
            const currentDimensionUnit = lastDimension.height.unit;

            return {
                ...prev,
                dimensions: [
                    ...prev.dimensions,
                    {
                        weight: { value: "", unit: currentWeightUnit },
                        length: { value: "", unit: currentDimensionUnit },
                        width: { value: "", unit: currentDimensionUnit },
                        height: { value: "", unit: currentDimensionUnit },
                        quantity: { value: "" },
                    },
                ],
            };
        });
    };

    const onRemoveDimension = (index) => {
        if (formData.dimensions.length > 1) {
            setFormData(prev => ({
                ...prev,
                dimensions: prev.dimensions.filter((_, i) => i !== index),
            }));
        }
    };

    return (
        <>
            <DHLService
                formData={{ ...formData, airfreight, totalDimensionsWeight }}
                trigger={fetchDHL}
                onData={(data) => {
                    setDhlResult(data);
                    setFetchDHL(false);
                }}
                onError={(error) => {
                    toast.error(`Failed to get estimation: ${error.message}`);
                    setFetchDHL(false);
                    setLoading(false);
                }}
                isGuest={isGuest}
            />
            {isPdfModalVisible && (
                <Modal
                    isOpen={isPdfModalVisible}
                    onRequestClose={() => setIsPdfModalVisible(false)}
                    style={{
                        content: { width: "60%", margin: "auto", height: "80vh" },
                        overlay: { backgroundColor: "rgba(0, 0, 0, 0.75)", zIndex: 1000 },
                    }}
                >
                    <iframe
                        src={`${currentPdfUrl}#toolbar=0`}
                        title="Estimate PDF"
                        style={{ width: "100%", height: "70vh", border: 0 }}
                    />
                </Modal>
            )}
            <Modal
                isOpen={isVisible}
                onRequestClose={onClose}
                style={{
                    content: {
                        top: "50%",
                        left: "50%",
                        right: "auto",
                        bottom: "auto",
                        marginRight: "-50%",
                        transform: "translate(-50%, -50%)",
                        backgroundColor: "white",
                        padding: "30px",
                        borderRadius: "12px",
                        maxWidth: "90%",
                        width: "850px",
                        maxHeight: "80vh",
                        overflowY: "auto",
                        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.08)",
                        border: "none",
                    },
                    overlay: { backgroundColor: "rgba(0, 0, 0, 0.75)", zIndex: 1000 },
                }}
            >
                <div className="flex flex-col">
                    <div className="mb-3">
                        <button
                            onClick={onBack || onClose}
                            className="btnBg text-white p-2 rounded-md flex items-center justify-center space-x-2"
                        >
                            <FaArrowLeft /> <span>Back</span>
                        </button>
                    </div>
                    <div className="flex flex-col space-y-4">
                        <EstimateFormDetails
                            isGuest={isGuest}
                            formData={formData}
                            setFormData={setFormData}
                            errors={errors}
                            setErrors={setErrors}
                            handleInputChange={handleInputChange}
                            isReadOnly={isReadOnly}
                        />
                        <EstimateFormDimensions
                            dimensions={formData.dimensions}
                            errors={errors}
                            isReadOnly={isReadOnly}
                            onDimensionChange={onDimensionChange}
                            onAddDimension={onAddDimension}
                            onRemoveDimension={onRemoveDimension}
                        />
                        <EstimateFormItems
                            items={formData.items}
                            itemsCurrency={formData.itemsCurrency}
                            selectedService={formData.selectedService}
                            errors={errors}
                            onInputChange={e => handleInputChange(e, setFormData, setErrors, needsStateSelection)}
                            onServiceInputChange={(index, field, e) => handleServiceInputChange(index, field, e, setFormData, setErrors)}
                            onAntdServiceInputChange={(index, field, value) => handleAntdServiceInputChange(index, field, value, setFormData)}
                            onAddItem={() => handleAddMore(setFormData)}
                            onRemoveItem={(index) => handleRemoveService(index, setFormData)}
                            isReadOnly={isReadOnly}
                            destinationCountry={formData.destinationCountry}
                        />
                        <EstimateFormOptions
                            formData={formData}
                            setFormData={setFormData}
                            errors={errors}
                            setErrors={setErrors}
                            isReadOnly={isReadOnly}
                            handleInputChange={handleInputChange}
                            onInsuranceChange={(value) => handleInsuranceChange(value, setFormData, totalItemsValue, setInsurance, airfreight, duty, collectionValue, setCif)}
                            eccnCodes={eccnCodes}
                        />
                        {showEstimateResults && (
                            <div className="mt-6">
                                {renderEstimateSummary()}
                            </div>
                        )}
                        <div className="flex justify-end mt-6">
                            <button
                                onClick={handleGetEstimate}
                                className="btnBg text-white p-2 rounded-md flex items-center justify-center"
                                disabled={loading}
                            >
                                {loading ? "Processing..." : buttonText}
                            </button>
                        </div>
                    </div>
                </div>
            </Modal>
            <Modal
                isOpen={isSuccessModalOpen}
                onRequestClose={() => { setIsSuccessModalOpen(false); onClose(); }}
                style={{
                    content: {
                        top: "50%",
                        left: "50%",
                        right: "auto",
                        bottom: "auto",
                        marginRight: "-50%",
                        transform: "translate(-50%, -50%)",
                        backgroundColor: "white",
                        padding: "20px",
                        borderRadius: "12px",
                        width: "400px",
                        textAlign: "center",
                    },
                    overlay: { backgroundColor: "rgba(0, 0, 0, 0.75)", zIndex: 1001 },
                }}
            >
                <div className="flex flex-col items-center">
                    <FaCheckCircle className="text-green-500 text-4xl mb-4" />
                    <h2 className="text-lg font-semibold mb-2">
                        {isNewEstimate ? "Estimate Submitted Successfully!" : "Estimate Updated Successfully!"}
                    </h2>
                    <p className="text-sm text-gray-600 mb-4">
                        {isGuest ? "Your estimate has been submitted." : "Admins have received your request. You'll be notified once the estimate is ready."}
                    </p>
                    <button
                        onClick={() => { setIsSuccessModalOpen(false); onClose(); }}
                        className="btnBg text-white p-2 rounded-md"
                    >
                        Close
                    </button>
                </div>
            </Modal>
            <ExternalWarningPopup
                isOpen={externalWarningPopup.isOpen}
                message={externalWarningPopup.message}
                onClose={() => setExternalWarningPopup({ isOpen: false, message: "" })}
            />
        </>
    );
};

export default EstimateFormComponent;